import type { SipSnapshot } from '../types';
import type { SipMediaController } from './SipMediaController';
import type { ConfiguredBrowserPhoneConfig } from './SipRegistrationController';
import type {
  SipDirection,
  SipLogger,
  SipSessionEvent,
  SipSessionPublicEvent,
  SipTimerPort,
} from './contracts';
import {
  createOutgoingRelayIceHandler,
  normalizeSessionSdpEvent,
  type SessionWebRtcCompatibility,
} from './WebRtcCompatibility';

const SIP_SESSION_TIMER_SECONDS = 120;
const RECOVERY_ANSWER_DELAY_MS = 150;

type SessionListener = {
  name: string;
  handler: (event?: any) => void;
};

type SessionBinding = {
  session: any;
  direction: SipDirection;
  generation: number;
  data: SipSessionEvent;
  compatibility: SessionWebRtcCompatibility;
  listeners: SessionListener[];
  recoveryTimer: number | null;
  startedAt: number | null;
  acceptedPublished: boolean;
  terminal: boolean;
};

type SessionMedia = Pick<SipMediaController,
  | 'prepareCallStream'
  | 'markCallInProgress'
  | 'rtcConfiguration'
  | 'attachRemoteAudio'
  | 'startQuality'
  | 'endSession'>;

export interface SipSessionControllerDeps {
  timers: SipTimerPort;
  now: () => number;
  logger: SipLogger;
  getConfig: () => ConfiguredBrowserPhoneConfig | null;
  isPrimaryTab: () => boolean;
  isRegistered: () => boolean;
  placeCall: (target: string, options: Record<string, unknown>) => any;
  media: SessionMedia;
  createCompatibility: (
    session: any,
    direction: SipDirection,
    isCurrent: () => boolean,
  ) => SessionWebRtcCompatibility;
  onSnapshot: (patch: Partial<SipSnapshot>) => void;
  onSessionEvent: (
    name: SipSessionPublicEvent,
    payload: SipSessionEvent,
  ) => void;
  onActivityChange: () => void;
}

function eventCause(event: any): string {
  return String(
    event?.cause
    || event?.data?.cause
    || event?.message?.reason_phrase
    || '',
  ).trim();
}

function sessionIdentifier(session: any): string {
  return String(
    session?.id
    || session?._request?.call_id
    || session?._dialog?.id?.call_id
    || globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random()}`,
  );
}

function remoteNumber(session: any): string {
  return String(
    session?.remote_identity?.uri?.user
    || session?.remote_identity?.display_name
    || session?._request?.from?.uri?.user
    || session?._request?.ruri?.user
    || '',
  );
}

function sessionHeader(session: any, name: string): string {
  const request = session?._request || session?.request || null;
  const direct = request?.getHeader?.(name) || request?.get_header?.(name);
  if (direct) return String(direct).trim();
  const key = String(name || '').toLowerCase();
  const raw = request?.headers?.[key] || request?.headers?.[name];
  const first = Array.isArray(raw) ? raw[0] : raw;
  return String(first?.raw || first?.value || first || '').trim();
}

function normalizeTarget(value: string, domain: string): string {
  const raw = String(value || '').trim();
  if (!raw) throw new Error('Не указан номер для звонка');
  if (/^sips?:/i.test(raw)) return raw;
  const normalized = raw.replace(/[^0-9+*#]/g, '');
  if (!normalized) throw new Error('Номер не содержит допустимых символов');
  return `sip:${normalized}@${domain}`;
}

function liveAudioTrack(stream: MediaStream): MediaStreamTrack | null {
  return stream.getAudioTracks().find(track => track.readyState === 'live') || null;
}

export class SipSessionController {
  private readonly timers: SipTimerPort;
  private readonly now: () => number;
  private readonly logger: SipLogger;
  private readonly getConfig: () => ConfiguredBrowserPhoneConfig | null;
  private readonly isPrimaryTab: () => boolean;
  private readonly isRegistered: () => boolean;
  private readonly placeCall: (
    target: string,
    options: Record<string, unknown>,
  ) => any;
  private readonly media: SessionMedia;
  private readonly createCompatibility: SipSessionControllerDeps['createCompatibility'];
  private readonly onSnapshot: (patch: Partial<SipSnapshot>) => void;
  private readonly onSessionEvent: SipSessionControllerDeps['onSessionEvent'];
  private readonly onActivityChange: () => void;
  private readonly pendingStops = new Set<any>();
  private readonly stoppedSessions = new WeakSet<object>();
  private current: SessionBinding | null = null;
  private answeringSession: any = null;
  private generation = 0;

  constructor(deps: SipSessionControllerDeps) {
    this.timers = deps.timers;
    this.now = deps.now;
    this.logger = deps.logger;
    this.getConfig = deps.getConfig;
    this.isPrimaryTab = deps.isPrimaryTab;
    this.isRegistered = deps.isRegistered;
    this.placeCall = deps.placeCall;
    this.media = deps.media;
    this.createCompatibility = deps.createCompatibility;
    this.onSnapshot = deps.onSnapshot;
    this.onSessionEvent = deps.onSessionEvent;
    this.onActivityChange = deps.onActivityChange;
  }

  handleNewSession(session: any, direction: SipDirection): void {
    if (!session || session === this.current?.session) return;

    if (this.current) {
      if (direction === 'INBOUND') {
        session.terminate?.({
          status_code: 486,
          reason_phrase: 'Busy Here',
        });
      }
      return;
    }

    const config = this.getConfig();
    if (direction === 'INBOUND' && !config?.account.allowInbound) {
      session.terminate?.({
        status_code: 486,
        reason_phrase: 'Inbound disabled',
      });
      return;
    }

    this.bindSession(session, direction);
  }

  async dial(
    value: string,
    options: { extraHeaders?: string[] } = {},
  ): Promise<any> {
    if (!this.isPrimaryTab()) {
      throw new Error('Телефон активен в другой вкладке');
    }
    const config = this.getConfig();
    if (!this.isRegistered() || !config?.account) {
      throw new Error('SIP-телефон не зарегистрирован');
    }
    if (!config.account.allowOutbound) {
      throw new Error('Исходящие звонки запрещены для пользователя');
    }
    if (this.current) throw new Error('Уже есть активный звонок');

    let createdSession: any = null;
    try {
      const stream = await this.media.prepareCallStream();
      const microphoneTrack = liveAudioTrack(stream);
      if (!microphoneTrack) {
        throw new Error('Микрофон не создал активную аудиодорожку.');
      }

      microphoneTrack.enabled = true;
      this.media.markCallInProgress();
      const target = normalizeTarget(value, config.sipDomain);
      const releaseOutgoingIce = createOutgoingRelayIceHandler(this.logger);

      createdSession = this.placeCall(target, {
        eventHandlers: {
          icecandidate: releaseOutgoingIce,
          sdp: (event: any) => {
            if (!normalizeSessionSdpEvent(event, createdSession)) return;
            const payload = event?.data && typeof event.data === 'object'
              ? event.data
              : event;
            const type = String(payload?.type || event?.type || '').toLowerCase();
            this.logger.info(
              `[telephony] FreeSWITCH remote SDP ${type || 'description'} normalized: restored BUNDLE/MID`,
            );
          },
        },
        sessionTimersExpires: SIP_SESSION_TIMER_SECONDS,
        mediaConstraints: { audio: true, video: false },
        mediaStream: stream,
        pcConfig: this.media.rtcConfiguration(config),
        extraHeaders: options.extraHeaders || [],
      });

      if (!createdSession) {
        throw new Error('АТС не создала исходящую SIP-сессию');
      }
      this.handleNewSession(createdSession, 'OUTBOUND');
      return createdSession;
    } catch (error) {
      if (!this.current) this.media.endSession();
      this.logger.error('[telephony] outbound call failed', error);
      throw error;
    }
  }

  async answer(): Promise<void> {
    const binding = this.current;
    const session = binding?.session;
    if (!binding || !session) {
      throw new Error('Входящая SIP-сессия ещё не получена');
    }

    const config = this.getConfig();
    if (!config) throw new Error('Настройки телефона не загружены');

    if (this.answeringSession === session) {
      this.logger.info('[telephony] v14.2 duplicate inbound answer ignored');
      return;
    }

    const waitingStatus = session?.C?.STATUS_WAITING_FOR_ANSWER;
    const acceptedStatuses = [
      session?.C?.STATUS_ANSWERED,
      session?.C?.STATUS_WAITING_FOR_ACK,
      session?.C?.STATUS_CONFIRMED,
    ].filter((value): value is number => typeof value === 'number');

    if (typeof waitingStatus === 'number' && session.status !== waitingStatus) {
      if (acceptedStatuses.includes(Number(session.status))) {
        this.logger.info(
          '[telephony] v14.2 inbound answer already in progress',
          session.status,
        );
        return;
      }
      throw new Error(
        `Входящий звонок уже недоступен (SIP status ${String(session.status)}).`,
      );
    }

    this.answeringSession = session;
    this.onActivityChange();

    try {
      const stream = await this.media.prepareCallStream();
      if (!this.isCurrent(binding)) {
        throw new Error('Входящий звонок завершён до соединения.');
      }
      const microphoneTrack = liveAudioTrack(stream);
      if (!microphoneTrack) {
        this.media.endSession();
        throw new Error('Микрофон не создал активную аудиодорожку.');
      }

      microphoneTrack.enabled = true;
      this.media.markCallInProgress();
      const accepted = binding.compatibility.waitForInboundAccepted();
      this.onSnapshot({ state: 'DIALING', error: '' });
      session.answer?.({
        sessionTimersExpires: SIP_SESSION_TIMER_SECONDS,
        mediaConstraints: { audio: true, video: false },
        mediaStream: stream,
        pcConfig: this.media.rtcConfiguration(config),
      });
      this.logger.info('[telephony] v14.2 inbound answer started');
      await accepted;
      this.logger.info('[telephony] v14.2 inbound answer accepted');
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Не удалось принять входящий звонок.';
      if (
        this.isCurrent(binding)
        && !session?.isEnded?.()
        && session.status === waitingStatus
      ) {
        this.onSnapshot({ state: 'RINGING', error: message });
      }
      this.logger.error('[telephony] v14.2 inbound answer failed', error);
      throw error;
    } finally {
      if (this.answeringSession === session) {
        this.answeringSession = null;
        this.onActivityChange();
      }
    }
  }

  decline(): void {
    this.current?.session?.terminate?.({
      status_code: 486,
      reason_phrase: 'Declined',
    });
  }

  hangup(): void {
    this.current?.session?.terminate?.();
  }

  mute(): void {
    const session = this.requireSession();
    session.mute?.({ audio: true });
  }

  unmute(): void {
    const session = this.requireSession();
    session.unmute?.({ audio: true });
  }

  hold(): void {
    const session = this.requireSession();
    const accepted = session.hold?.({ useUpdate: false });
    if (accepted === false) {
      throw new Error('SIP-сессия пока не готова к удержанию');
    }
  }

  unhold(): void {
    const session = this.requireSession();
    const accepted = session.unhold?.({ useUpdate: false });
    if (accepted === false) {
      throw new Error('SIP-сессия пока не готова к возврату с удержания');
    }
  }

  sendDtmf(tone: string): void {
    const session = this.requireSession();
    if (!/^[0-9A-D*#]$/i.test(tone)) {
      throw new Error('Недопустимый DTMF-сигнал');
    }
    session.sendDTMF?.(tone, { duration: 160, interToneGap: 70 });
  }

  hasSession(): boolean {
    return Boolean(this.current);
  }

  hasLiveActivity(): boolean {
    return Boolean(this.current || this.answeringSession);
  }

  prepareStop(): void {
    this.generation += 1;
    const binding = this.current;
    this.current = null;
    this.answeringSession = null;
    if (binding) {
      binding.terminal = true;
      this.disposeBinding(binding);
      this.pendingStops.add(binding.session);
    }
    this.onActivityChange();
  }

  finishStop(): void {
    for (const session of this.pendingStops) {
      this.pendingStops.delete(session);
      if (
        (typeof session === 'object' || typeof session === 'function')
        && session !== null
        && !this.stoppedSessions.has(session)
      ) {
        this.stoppedSessions.add(session);
        try {
          session.terminate?.();
        } catch (error) {
          this.logger.warn('[telephony] SIP session stop failed', error);
        }
      }
    }
  }

  stop(): void {
    this.prepareStop();
    this.finishStop();
  }

  private bindSession(session: any, direction: SipDirection): void {
    const generation = ++this.generation;
    const recovery = /^(?:1|true|yes)$/i.test(
      sessionHeader(session, 'X-Intez-Recovery'),
    );
    const originalDirectionHeader = sessionHeader(
      session,
      'X-Intez-Original-Direction',
    ).toUpperCase();
    const originalDirection: SipDirection = originalDirectionHeader === 'OUTBOUND'
      ? 'OUTBOUND'
      : originalDirectionHeader === 'INBOUND'
        ? 'INBOUND'
        : direction;
    const data: SipSessionEvent = {
      session,
      direction,
      sessionId: sessionIdentifier(session),
      remoteNumber: sessionHeader(session, 'X-Intez-Remote-Number')
        || remoteNumber(session),
      recovery,
      callId: sessionHeader(session, 'X-Intez-Call-Id') || undefined,
      anchorId: sessionHeader(session, 'X-Intez-Anchor-Id') || undefined,
      externalUuid: sessionHeader(session, 'X-Intez-External-Uuid') || undefined,
      originalDirection,
    };
    let binding!: SessionBinding;
    const compatibility = this.createCompatibility(
      session,
      direction,
      () => this.isCurrent(binding),
    );
    binding = {
      session,
      direction,
      generation,
      data,
      compatibility,
      listeners: [],
      recoveryTimer: null,
      startedAt: null,
      acceptedPublished: false,
      terminal: false,
    };
    this.current = binding;
    if (direction === 'INBOUND') this.answeringSession = null;
    compatibility.bind();

    this.onSnapshot({
      state: direction === 'INBOUND' ? 'RINGING' : 'DIALING',
      sessionDirection: direction,
      remoteNumber: data.remoteNumber,
      muted: false,
      held: false,
      startedAt: null,
      endedAt: null,
      error: '',
    });
    this.onSessionEvent('sessionNew', data);
    this.onActivityChange();

    if (recovery && direction === 'INBOUND') {
      binding.recoveryTimer = this.timers.setTimeout(() => {
        binding.recoveryTimer = null;
        if (!this.isCurrent(binding)) return;
        void this.answer().catch(error => {
          if (!this.isCurrent(binding)) return;
          const message = error instanceof Error
            ? error.message
            : 'Не удалось автоматически восстановить разговор';
          this.onSnapshot({ error: message });
          this.logger.warn(
            '[telephony] automatic call recovery answer failed',
            error,
          );
        });
      }, RECOVERY_ANSWER_DELAY_MS);
    }

    this.addListener(binding, 'peerconnection', () => {
      this.media.attachRemoteAudio(session);
    });
    this.addListener(binding, 'connecting', () => {
      this.media.attachRemoteAudio(session);
    });
    this.addListener(binding, 'progress', () => {
      this.onSnapshot({
        state: direction === 'INBOUND' ? 'RINGING' : 'DIALING',
      });
    });
    this.addListener(binding, 'accepted', () => {
      binding.startedAt ||= this.now();
      this.onSnapshot({
        state: 'ACTIVE',
        startedAt: binding.startedAt,
        endedAt: null,
        error: '',
      });
      this.media.attachRemoteAudio(session);
      this.media.startQuality(session);
      if (!binding.acceptedPublished) {
        binding.acceptedPublished = true;
        this.onSessionEvent('sessionAccepted', data);
      }
    });
    this.addListener(binding, 'confirmed', () => {
      binding.startedAt ||= this.now();
      this.onSnapshot({
        state: 'ACTIVE',
        startedAt: binding.startedAt,
        endedAt: null,
        error: '',
      });
      this.media.attachRemoteAudio(session);
      this.media.startQuality(session);
    });
    this.addListener(binding, 'hold', () => {
      this.onSnapshot({ held: true, state: 'HOLD' });
    });
    this.addListener(binding, 'unhold', () => {
      this.onSnapshot({ held: false, state: 'ACTIVE' });
    });
    this.addListener(binding, 'muted', () => {
      this.onSnapshot({ muted: true });
    });
    this.addListener(binding, 'unmuted', () => {
      this.onSnapshot({ muted: false });
    });
    this.addListener(binding, 'ended', event => {
      this.finishTerminal(binding, 'sessionEnded', {
        ...data,
        cause: eventCause(event),
      });
    });
    this.addListener(binding, 'failed', event => {
      const cause = eventCause(event) || 'Ошибка SIP-вызова';
      const reasonHeader = String(
        event?.message?.getHeader?.('Reason')
        || event?.data?.message?.getHeader?.('Reason')
        || '',
      );
      const answeredElsewhere = direction === 'INBOUND'
        && /call completed elsewhere|answered elsewhere|cause\s*=\s*200/i.test(
          reasonHeader,
        );
      this.finishTerminal(binding, 'sessionFailed', {
        ...data,
        cause,
        answeredElsewhere,
      });
    });
  }

  private addListener(
    binding: SessionBinding,
    name: string,
    callback: (event?: any) => void,
  ): void {
    const handler = (event?: any) => {
      if (!this.isCurrent(binding) || binding.terminal) return;
      callback(event);
    };
    binding.session?.on?.(name, handler);
    binding.listeners.push({ name, handler });
  }

  private finishTerminal(
    binding: SessionBinding,
    name: 'sessionEnded' | 'sessionFailed',
    payload: SipSessionEvent,
  ): void {
    if (!this.isCurrent(binding) || binding.terminal) return;
    binding.terminal = true;
    this.generation += 1;
    this.current = null;
    if (this.answeringSession === binding.session) this.answeringSession = null;
    this.disposeBinding(binding);
    this.media.endSession();
    this.onSnapshot({
      state: this.isRegistered() ? 'READY' : 'OFFLINE',
      startedAt: null,
      endedAt: null,
      error: '',
      sessionDirection: null,
      remoteNumber: '',
      muted: false,
      held: false,
    });
    this.onActivityChange();
    this.onSessionEvent(name, payload);
  }

  private disposeBinding(binding: SessionBinding): void {
    if (binding.recoveryTimer !== null) {
      this.timers.clearTimeout(binding.recoveryTimer);
      binding.recoveryTimer = null;
    }
    try {
      binding.compatibility.dispose();
    } catch (error) {
      this.logger.warn('[telephony] session compatibility cleanup failed', error);
    }
    for (const { name, handler } of binding.listeners.splice(0)) {
      try {
        binding.session?.removeListener?.(name, handler);
      } catch {
        // JsSIP cleanup remains best effort during terminal races.
      }
    }
  }

  private isCurrent(binding: SessionBinding): boolean {
    return Boolean(
      this.current === binding
      && this.generation === binding.generation
      && !binding.terminal,
    );
  }

  private requireSession(): any {
    const session = this.current?.session;
    if (!session) throw new Error('Нет активного звонка');
    return session;
  }
}
